import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import Toast from "../../components/Common/Toast";
import commonService from "../../services/otherServices";
import { getErrorMessage, getSenderUpdateErrorMessage } from "../../services/apiClient";
import { FlaskConical, TriangleAlert, Clock, SendHorizontal, X } from "lucide-react";

const emptyForm = {
  sender_name: "",
  sender_email: "",
  receiver_email_1: "",
  receiver_email_2: "",
  subject: "Test subject",
  body: "Test body",
};

const AdminTestSendPage: React.FC = () => {
  const [form, setForm] = useState(emptyForm);
  const [salesforceAccounts, setSalesforceAccounts] = useState<{ account_number: number; email: string }[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<number | "">("");

  const [showConfirm, setShowConfirm] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState<any>(null);

  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [verificationLogs, setVerificationLogs] = useState<string[]>([]);
  const [sendMethod, setSendMethod] = useState<"automation" | "api">("automation");

  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  useEffect(() => {
    commonService
      .getSalesforceAccounts()
      .then((res) => setSalesforceAccounts(res.data?.data || []))
      .catch((err) =>
        setToast({ type: "error", message: getErrorMessage(err, "Failed to load Salesforce accounts") })
      );

    commonService
      .getSalesforceSendMethod()
      .then((res) => setSendMethod(res.data?.send_method || "automation"))
      .catch(() => {});
  }, []);

  const receiverEmails = () =>
    [form.receiver_email_1, form.receiver_email_2].map((e) => e.trim()).filter(Boolean);

  const validate = () => {
    if (!form.sender_name.trim() || !form.sender_email.trim() || !form.receiver_email_1.trim()) {
      setToast({ type: "error", message: "Sender name, sender email and at least one receiver email are required" });
      return false;
    }

    if (!selectedAccount) {
      setToast({ type: "error", message: "Select a Salesforce account" });
      return false;
    }

    return true;
  };

  const openConfirm = () => {
    if (!validate()) return;
    setShowConfirm(true);
  };

  // Real browser-driven sender verification - used for automation. Real
  // campaigns already go through this before Launch (CampaignReview.tsx's
  // verifySenderEmailAutomation). Test Send is not allowed to skip it just
  // because it's a test.
  const verifySenderEmailAutomation = (accountNumber: number, sequenceId: number) => {
    return new Promise<boolean>((resolve) => {
      setVerificationLogs([]);
      setIsVerifyingEmail(true);

      commonService.emailUpdateSocket(
        {
          new_name: form.sender_name,
          new_email: form.sender_email,
          sequence_id: sequenceId,
          salesforce_account: accountNumber,
        },
        {
          onLog: (message: string) => {
            setVerificationLogs((prev) => [...prev, message]);
          },
          onCompleted: (success: boolean) => {
            setIsVerifyingEmail(false);
            resolve(success);
          },
          onError: (message: string) => {
            setIsVerifyingEmail(false);
            setToast({ type: "error", message });
            resolve(false);
          },
        }
      );
    });
  };

  // Direct API sender-identity update - PATCHes this account's own
  // SenderName/SenderEmail (the same "My Email Settings" automation
  // changes via the browser). If the address isn't already verified for
  // this user, Salesforce emails a confirmation link there and won't send
  // as this identity until it's clicked - so after triggering the update,
  // poll status every few seconds for up to ~2 minutes for that click.
  const verifySenderEmailApi = async (accountNumber: number) => {
    setVerificationLogs([]);
    setIsVerifyingEmail(true);

    try {
      const triggerRes = await commonService.updateSenderEmailApi({
        account: accountNumber,
        display_name: form.sender_name,
        email: form.sender_email,
      });

      setVerificationLogs((prev) => [...prev, triggerRes.data?.message || "Sender identity update triggered."]);

      if (triggerRes.data?.is_verified) {
        setIsVerifyingEmail(false);
        return true;
      }

      setVerificationLogs((prev) => [
        ...prev,
        `Waiting for ${form.sender_email} to confirm the Salesforce verification link (checking every 5s, up to 2 minutes)...`,
      ]);

      const maxAttempts = 24;
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, 5000));

        const statusRes = await commonService.getSenderEmailApiStatus({
          account: accountNumber,
          display_name: form.sender_name,
          email: form.sender_email,
        });

        if (statusRes.data?.is_verified) {
          setVerificationLogs((prev) => [...prev, "Confirmed - sender identity is now active."]);
          setIsVerifyingEmail(false);
          return true;
        }
      }

      setVerificationLogs((prev) => [...prev, "Still not confirmed after 2 minutes - please retry once the link is clicked."]);
      setIsVerifyingEmail(false);
      return false;
    } catch (error) {
      setIsVerifyingEmail(false);
      setToast({ type: "error", message: getSenderUpdateErrorMessage(error, "Sender email update failed") });
      return false;
    }
  };

  const verifySenderEmail = (accountNumber: number, sequenceId: number) => {
    return sendMethod === "api"
      ? verifySenderEmailApi(accountNumber)
      : verifySenderEmailAutomation(accountNumber, sequenceId);
  };

  const handleSend = async () => {
    setIsSending(true);
    setShowConfirm(false);

    try {
      // Step 1: create the draft - not sent yet, not picked up by the
      // worker until confirmed below.
      const createRes = await commonService.testSendCampaign({
        sender_name: form.sender_name,
        sender_email: form.sender_email,
        receiver_emails: receiverEmails(),
        subject: form.subject,
        body: form.body,
        salesforce_account: Number(selectedAccount),
      });

      const sequenceId = createRes.data?.sequence_id;

      // Step 2: verify the sender email via the real Salesforce browser
      // session - always, regardless of send_method. Personal "My Email
      // Settings" (what this actually changes) has no confirmed Salesforce
      // API at all, so browser automation is the only working mechanism
      // for this specific step; lead upload and the actual send below
      // still go fully through the API when send_method is "api".
      const verified = await verifySenderEmail(Number(selectedAccount), sequenceId);

      if (!verified) {
        // A "draft" row is invisible to the background worker - without
        // explicitly closing it here, this campaign would sit stuck at
        // "inprogress" forever with no way to close it out.
        await commonService.abandonTestSend(sequenceId).catch(() => {});

        setResult({ ok: false, error: "Sender email verification failed - test send was not queued." });
        setToast({ type: "error", message: "Sender email verification failed" });
        return;
      }

      // Step 3: only now confirm - flips draft to scheduled, worker picks
      // it up within ~10s.
      const confirmRes = await commonService.confirmTestSend(sequenceId);

      setResult({ ok: true, data: confirmRes.data });
      setToast({ type: "success", message: "Sender verified - test send confirmed and queued" });
    } catch (error) {
      const message = getSenderUpdateErrorMessage(error, "Test send failed");
      setResult({ ok: false, error: message });
      setToast({ type: "error", message });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <AdminLayout>
      <div style={{ padding: "28px", maxWidth: "720px" }}>
        <div style={{ marginBottom: "22px" }}>
          <div style={{ fontSize: "26px", fontWeight: 800, color: "#111827", display: "flex", alignItems: "center", gap: "10px" }}>
            <FlaskConical size={24} color="#7c3aed" />
            Test Send
          </div>
          <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "4px" }}>
            Runs the exact same Salesforce send pipeline as a real campaign — skips scraping and verification only.
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "flex-start",
            background: "#fef3c7",
            border: "1px solid #fde68a",
            borderRadius: "12px",
            padding: "14px 16px",
            marginBottom: "20px",
          }}
        >
          <TriangleAlert size={18} color="#b45309" style={{ flexShrink: 0, marginTop: "2px" }} />
          <div style={{ fontSize: "13px", color: "#92400e", lineHeight: 1.5 }}>
            This logs into a <strong>real Salesforce account</strong> and sends a <strong>real email</strong> to
            the receiver address below. Use a test inbox you control, not a real prospect's address.
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "flex-start",
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            borderRadius: "12px",
            padding: "14px 16px",
            marginBottom: "20px",
          }}
        >
          <Clock size={18} color="#1d4ed8" style={{ flexShrink: 0, marginTop: "2px" }} />
          <div style={{ fontSize: "13px", color: "#1e40af", lineHeight: 1.5 }}>
            <strong>This takes 1–2 minutes.</strong> Salesforce needs you to confirm the sender email
            before it will send anything. <strong>What to do:</strong> right after you click send, open
            the sender's inbox and click the confirmation link Salesforce emails there — the send won't
            go through until you do.
          </div>
        </div>

        <div
          style={{
            background: "white",
            borderRadius: "16px",
            border: "1px solid #e5e7eb",
            padding: "22px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
            <Field label="Sender Name">
              <input
                value={form.sender_name}
                onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <Field label="Sender Email">
              <input
                type="email"
                value={form.sender_email}
                onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
                style={inputStyle}
              />
            </Field>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
            <Field label="Receiver Email 1 (where the test email will actually land)">
              <input
                type="email"
                value={form.receiver_email_1}
                onChange={(e) => setForm({ ...form, receiver_email_1: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <Field label="Receiver Email 2 (optional — leave blank for a single-lead test)">
              <input
                type="email"
                value={form.receiver_email_2}
                onChange={(e) => setForm({ ...form, receiver_email_2: e.target.value })}
                style={inputStyle}
              />
            </Field>
          </div>

          <Field label="Salesforce Account">
            <select
              value={selectedAccount}
              onChange={(e) => setSelectedAccount(e.target.value ? Number(e.target.value) : "")}
              style={inputStyle}
            >
              <option value="">Select an account</option>
              {salesforceAccounts.map((acc) => (
                <option key={acc.account_number} value={acc.account_number}>
                  {`Account ${acc.account_number} — ${acc.email}`}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Subject">
            <input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              style={inputStyle}
            />
          </Field>

          <Field label="Body">
            <textarea
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              style={{ ...inputStyle, minHeight: "120px", resize: "vertical", fontFamily: "inherit" }}
            />
          </Field>

          <div>
            <button onClick={openConfirm} disabled={isSending} style={primaryBtn}>
              <SendHorizontal size={15} />
              {isSending ? "Sending..." : "Send Test Email"}
            </button>
          </div>
        </div>

        {result && (
          <div
            style={{
              marginTop: "20px",
              background: result.ok ? "#f0fdf4" : "#fef2f2",
              border: `1px solid ${result.ok ? "#bbf7d0" : "#fecaca"}`,
              borderRadius: "12px",
              padding: "16px",
            }}
          >
            <div style={{ fontWeight: 700, color: result.ok ? "#166534" : "#991b1b", marginBottom: "8px" }}>
              {result.ok ? "Test send completed" : "Test send failed"}
            </div>
            <pre style={{ margin: 0, fontSize: "12px", whiteSpace: "pre-wrap", color: "#374151" }}>
              {JSON.stringify(result.ok ? result.data : result.error, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* CONFIRM MODAL */}
      {showConfirm && (
        <div style={modalOverlay}>
          <div style={modalCard}>
            <div style={modalHeader}>
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700 }}>Confirm Test Send</h3>
              <button onClick={() => setShowConfirm(false)} style={closeBtn}>
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: "22px", fontSize: "14px", color: "#374151", lineHeight: 1.6 }}>
              This will send a real email from <strong>{form.sender_email}</strong> to{" "}
              <strong>{receiverEmails().join(", ")}</strong> via Salesforce account{" "}
              <strong>{selectedAccount}</strong>. This action can't be undone. Continue?
            </div>

            <div style={modalFooter}>
              <button onClick={() => setShowConfirm(false)} style={secondaryBtn}>
                Cancel
              </button>
              <button onClick={handleSend} style={{ ...primaryBtn, background: "#b45309" }}>
                <SendHorizontal size={15} />
                Yes, send it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SENDER EMAIL VERIFICATION PROGRESS */}
      {isVerifyingEmail && (
        <div style={modalOverlay}>
          <div style={{ ...modalCard, maxWidth: "620px" }}>
            <div style={modalHeader}>
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700 }}>Email Verification</h3>
            </div>

            <div style={{ padding: "18px 22px", fontSize: "13px", color: "#6b7280" }}>
              Please confirm the verification link Salesforce sends to <strong>{form.sender_email}</strong>.
            </div>

            <div style={{ padding: "16px 22px", background: "#0f172a", maxHeight: "360px", overflowY: "auto" }}>
              {verificationLogs.length === 0 ? (
                <div style={{ color: "#94a3b8", fontSize: "13px", fontFamily: "monospace" }}>Starting...</div>
              ) : (
                verificationLogs.map((log, index) => (
                  <div
                    key={index}
                    style={{
                      color: "#e2e8f0",
                      fontSize: "13px",
                      marginBottom: "10px",
                      fontFamily: "monospace",
                      lineHeight: "20px",
                      wordBreak: "break-word",
                    }}
                  >
                    {log}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
    </AdminLayout>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "6px", fontWeight: 600 }}>{label}</div>
    {children}
  </div>
);

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  border: "1px solid #d1d5db",
  borderRadius: "8px",
  outline: "none",
  fontSize: "14px",
  boxSizing: "border-box",
};

const primaryBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  background: "#4338ca",
  color: "#fff",
  border: "none",
  borderRadius: "10px",
  padding: "11px 18px",
  fontWeight: 600,
  fontSize: "14px",
  cursor: "pointer",
};

const secondaryBtn: React.CSSProperties = {
  background: "#f3f4f6",
  color: "#111827",
  border: "none",
  borderRadius: "10px",
  padding: "10px 16px",
  fontWeight: 600,
  fontSize: "13px",
  cursor: "pointer",
};

const modalOverlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(15,23,42,0.55)",
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px",
  backdropFilter: "blur(4px)",
};

const modalCard: React.CSSProperties = {
  width: "100%",
  maxWidth: "460px",
  background: "#fff",
  borderRadius: "18px",
  overflow: "hidden",
  boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
};

const modalHeader: React.CSSProperties = {
  padding: "18px 22px",
  borderBottom: "1px solid #e5e7eb",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const modalFooter: React.CSSProperties = {
  padding: "18px 22px",
  borderTop: "1px solid #e5e7eb",
  display: "flex",
  justifyContent: "flex-end",
  gap: "10px",
};

const closeBtn: React.CSSProperties = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: "#6b7280",
};

export default AdminTestSendPage;
